// Laravel's VerifyCsrfToken reads this plaintext header BEFORE it falls back to decrypting
// X-XSRF-TOKEN, the copy of the XSRF-TOKEN cookie that axios attaches on its own.
const CSRF_HEADER = 'X-CSRF-TOKEN'

// Laravel does not check the token on these.
const READ_METHODS = new Set(['get', 'head', 'options'])

/** Whether `url`, resolved the way the browser will resolve it, is on this page's origin. */
export function isSameOrigin(url, baseURL) {
    if (typeof window === 'undefined') {
        return false
    }

    try {
        const base = baseURL ? new URL(baseURL, window.location.href) : window.location.href

        return new URL(url ?? '', base).origin === window.location.origin
    } catch {
        return false
    }
}

/**
 * SM-9775. Every same-origin, state-changing request sent through `instance` carries the
 * session's plaintext CSRF token, as the app's own axios instance (api/axios.js) already does.
 *
 * bootstrap.js installs this on the bare axios export, which is what Inertia's router sends
 * through (`$inertia.post`, `router.*`, `useForm().submit`) and which used to send only the
 * XSRF-TOKEN cookie. Production and staging write that cookie for the whole `.marketscale.com`
 * domain and sandbox writes its own for its host, so a browser that has used either sends
 * sandbox two cookies of that name. axios copies the first one listed, the older one, which
 * sandbox cannot decrypt: every Inertia form submit answered 419, and a retry or a reload did
 * not help. Laravel checks the plaintext token first, so the stray cookie no longer matters.
 *
 * `getToken` reads the Inertia `csrf_token` prop, which is only as fresh as the last Inertia
 * response. Every sign-in rotates the session's token, "View as" and leaving it included, so a
 * page rendered before one in another tab holds a stale token. Laravel refuses a stale
 * plaintext token even beside a good cookie, so that 419 is retried once without the header:
 * the cookie-only request this used to send. A token the caller set itself is left alone.
 *
 * Returns a function that removes both interceptors again.
 */
export function sendPlaintextCsrfToken(instance, getToken) {
    const request = instance.interceptors.request.use((config) => {
        const method = String(config.method ?? 'get').toLowerCase()

        if (
            config._skipPlaintextCsrf
            || READ_METHODS.has(method)
            || config.headers.has(CSRF_HEADER)
            || !isSameOrigin(config.url, config.baseURL)
        ) {
            return config
        }

        const token = getToken()

        if (typeof token === 'string' && token !== '') {
            config.headers.set(CSRF_HEADER, token)
            config._plaintextCsrf = true
        }

        return config
    })

    const response = instance.interceptors.response.use(undefined, (error) => {
        const config = error?.config

        if (error?.response?.status !== 419 || !config?._plaintextCsrf || config._skipPlaintextCsrf) {
            return Promise.reject(error)
        }

        config._skipPlaintextCsrf = true
        config.headers.delete(CSRF_HEADER)

        return instance.request(config)
    })

    return () => {
        instance.interceptors.request.eject(request)
        instance.interceptors.response.eject(response)
    }
}
